import React from "react";
import PageSeo from "../seo/PageSeo";
import { DEFAULT_OG_IMAGE, SITE_URL } from "../seo/constants";
import HomeHero from "../components/HomeHero";
import PageLayout from "../components/PageLayout";
import { SOCIAL_PROFILE_URLS } from "../site/contact";
import {
    Typography,
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Home: React.FC = () => {
    const pageDescription =
        'Queer-owned standard poodle breeder in Austin, TX. Focused on conformation, temperament, and health testing for well-rounded dogs.';

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <PageSeo
                title="Galia Standard Poodles | Austin, TX Breeder"
                description={pageDescription}
                path="/"
                image={DEFAULT_OG_IMAGE}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'LocalBusiness',
                    name: 'Galia Standard Poodles',
                    url: SITE_URL,
                    image: DEFAULT_OG_IMAGE,
                    description: pageDescription,
                    address: {
                        '@type': 'PostalAddress',
                        addressLocality: 'Austin',
                        addressRegion: 'TX',
                        addressCountry: 'US',
                    },
                    sameAs: [...SOCIAL_PROFILE_URLS],
                }}
            />

            <HomeHero />

            <PageLayout variant="homeBand">
                    <Typography
                        component="h1"
                        variant="h4"
                        gutterBottom
                    >
                        Welcome to Galia Standard Poodles
                    </Typography>

                    <Typography variant="body1" paragraph>
                        Based in Austin, TX, we are a dedicated, small breeder with a passion for raising loving and well-rounded dogs. As a queer-owned business, we celebrate diversity and inclusivity, and do not discriminate for any reason. We raise our dogs in our own home, focusing on nurturing traits that ensure our poodles are successful in the AKC and UKC show rings, dog sports, and therapy work.
                    </Typography>

                    <Typography component="h2" variant="h6" gutterBottom>
                        Early Puppy Development
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="Grooming desensitization starting at 3 weeks" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="Early Neurological Stimulation (ENS)" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="Scent Introduction (ESI) from day 3" />
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 3 }} />

                    <Typography component="h2" variant="h6" gutterBottom>
                        Health Testing & Temperament
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Our breeding program includes comprehensive health testing, following the guidelines set by the OFA. Each of our poodles holds a CHIC number, and we go above and beyond the CHIC requirements.
                    </Typography>

                    <Typography variant="body1" paragraph>
                        We also conduct Volhard Temperament Testing to match each puppy to a suitable home. Occasionally, we identify poodles with the potential to be service dog prospects. These puppies are only placed in homes that provide the right environment and support.
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        bgcolor: '#f9f9f9',
                        p: 2,
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        my: 2,
                    }}>
                        <Typography variant="body1" fontStyle="italic">
                            “We breed for conformation and preservation—not trends or designer mixes.”
                        </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Learn more about <a href="https://www.ofa.org" target="_blank" rel="noopener noreferrer">OFA & CHIC testing</a> or <a href="https://www.akc.org/expert-advice/dog-breeding/puppy-temperament-tests-tool-help-placement/#:~:text=The%20Volhard%20Puppy%20Aptitude%20Test,%2C%20sight%20sensitivity%2C%20and%20stability." target="_blank" rel="noopener noreferrer">Volhard Temperament Testing</a>.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Typography component="h2" variant="h6" gutterBottom>
                        Our Commitment
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Galia Standard Poodles does not sell to individuals producing cross-bred dogs or doodles. While we focus on solid colors, we are multicolor poodle-friendly—excluding merle. The merle gene can cause genetic diseases and is not naturally found in poodles.
                    </Typography>
            </PageLayout>
        </Box>
    );
};

export default Home;
